/**
 * Workflow Panel Types
 * TypeScript types mirroring the API shapes from workflow.schemas.ts
 */

import { realUnitNumber } from '@resida/shared';
import { z } from 'zod';
import {
  WorkflowActionSchema,
  PreconditionSchema,
  MemberWorkflowSummarySchema,
  SagaWorkflowSummarySchema,
  StandaloneWorkflowSummarySchema,
  EnsureWorkflowBodySchema,
} from '@resida/api-types/workflow';

// ── WorkflowAction discriminated union ────────────────────────────────────────

// Derived from WorkflowActionSchema in @resida/api-types/workflow
export type WorkflowAction = z.infer<typeof WorkflowActionSchema>;

// ── Precondition ───────────────────────────────────────────────────────────────

// Derived from PreconditionSchema in @resida/api-types/workflow
export type Precondition = z.infer<typeof PreconditionSchema>;

// ── Member workflow ────────────────────────────────────────────────────────────

export type WorkflowStatus = 'active' | 'completed' | 'upcoming' | 'dismissed';

// Derived from MemberWorkflowSummarySchema in @resida/api-types/workflow
export type MemberWorkflowSummary = z.infer<typeof MemberWorkflowSummarySchema>;

// ── Saga workflow ──────────────────────────────────────────────────────────────

// Derived from SagaWorkflowSummarySchema in @resida/api-types/workflow
export type SagaWorkflowSummary = z.infer<typeof SagaWorkflowSummarySchema>;

// ── Standalone atomic workflow (top-level, not wrapped in a saga) ───────────────

// Derived from StandaloneWorkflowSummarySchema in @resida/api-types/workflow
export type AtomicWorkflowSummary = z.infer<typeof StandaloneWorkflowSummarySchema>;

// ── Union type for list items ──────────────────────────────────────────────────

export type WorkflowListItem = SagaWorkflowSummary | AtomicWorkflowSummary;

/**
 * UI-owned display fallback for a saga whose unit has no label (`unitLabel === null`), keyed by
 * saga type. These are pure presentation strings — the API never sends them, so there is no
 * backend literal to keep in sync. `unitLabel === null` (not a string compare) is the single
 * source of "this saga has no unit label". Keyed by type (not a single constant) since a
 * `lease_end` saga must not fall back to "Move-in" (issue #1027).
 */
export const SAGA_FALLBACK_LABELS: Record<SagaWorkflowSummary['type'], string> = {
  move_in: 'Move-in',
  lease_end: 'Lease end',
};

/** Human-friendly labels for standalone atomic workflows shown at the list level. */
export const ATOMIC_WORKFLOW_LABELS: Record<AtomicWorkflowSummary['type'], string> = {
  lease_setup: 'Set up lease',
  payment_setup: 'Set up payments',
  move_in_walkthrough: 'Move-In Walkthrough',
  move_out_walkthrough: 'Move-Out Walkthrough',
};

/** Resolve a display label for any top-level workflow row (saga or standalone atomic). */
export function workflowDisplayLabel(workflow: WorkflowListItem): string {
  return workflow.kind === 'saga'
    ? realUnitNumber(workflow.unitLabel) ?? SAGA_FALLBACK_LABELS[workflow.type]
    : ATOMIC_WORKFLOW_LABELS[workflow.type];
}

/**
 * Whether this unit already has a lease_setup workflow tracked — either as a member of a
 * non-dismissed `move_in` saga, or as a standalone atomic workflow — so the standalone
 * "Start guided lease setup" CTA must stay hidden (issue #715).
 *
 * Deliberately checks MEMBERSHIP only, not member `status`. A saga's `lease_setup` member is
 * lazily instantiated by GET /workflows (resolveSagaMembers) and can sit as `status: 'upcoming'`
 * (no row yet) for a while after the saga itself is created. `lease_setup`'s dedupeKey is a
 * canonical, server-derived convergence key (deriveCanonicalDedupeKey) — the SAME key the saga
 * will eventually use for its member. If the standalone CTA fires POST /workflows/ensure while
 * the member is still 'upcoming', it claims that dedupeKey slot with a `parentId: null` row.
 * The saga's later attempt to lazily create its member (parentId = sagaRow.id) then collides on
 * the (type, dedupeKey) unique constraint and silently no-ops (BR-JO-01 ON CONFLICT DO NOTHING),
 * permanently locking the member as 'upcoming' with no preconditions/CTAs. Gating on saga
 * membership (any status) rather than `status === 'active'` closes that window entirely: the
 * button hides the moment the saga (and its placeholder member) appears in the list, not only
 * once the member has been resolved to 'active'.
 */
export function hasActiveLeaseSetupSaga(
  workflows: WorkflowListItem[],
  unitId: string | undefined,
): boolean {
  if (!unitId) return false;
  return workflows.some(
    (w) =>
      w.status !== 'dismissed' &&
      w.subjectId === unitId &&
      w.members.some((m) => m.type === 'lease_setup'),
  );
}

// ── Ensure workflow input ──────────────────────────────────────────────────────

// Derived from EnsureWorkflowBodySchema in @resida/api-types/workflow
export type EnsureWorkflowInput = z.infer<typeof EnsureWorkflowBodySchema>;
